"use client";

import { useState } from "react";
import {
  Building2,
  TrendingUp,
  TrendingDown,
  Calendar,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Zap,
  ExternalLink,
} from "lucide-react";
import { ContextAnalysis, FinanceData } from "@/hooks/useAIContextAnalysis";

interface Market {
  id: string;
  question: string;
  outcomes: string[];
  outcomePrices: number[];
}

interface FinanceAISidebarProps {
  analysis: ContextAnalysis;
  eventTitle: string;
  marketQuestions?: string[];
  markets?: Market[];
}

function getCompanyPrice(
  companyName: string,
  markets?: Market[]
): number | null {
  if (!markets) return null;
  const nameLower = companyName.toLowerCase();
  for (const market of markets) {
    const outcomeIndex = market.outcomes.findIndex(
      (o) => o.toLowerCase() === nameLower
    );
    if (outcomeIndex !== -1 && market.outcomePrices[outcomeIndex] != null) {
      return market.outcomePrices[outcomeIndex];
    }
    if (
      market.question.toLowerCase().includes(nameLower) &&
      market.outcomePrices[0] != null
    ) {
      return market.outcomePrices[0];
    }
  }
  return null;
}

function formatMarketCap(raw?: number): string {
  if (!raw) return "";
  if (raw >= 1e12) return `$${(raw / 1e12).toFixed(1)}T`;
  if (raw >= 1e9) return `$${(raw / 1e9).toFixed(0)}B`;
  if (raw >= 1e6) return `$${(raw / 1e6).toFixed(0)}M`;
  return `$${raw}`;
}

export default function FinanceAISidebar({
  analysis,
  eventTitle,
  marketQuestions = [],
  markets = [],
}: FinanceAISidebarProps) {
  const [showAllCompanies, setShowAllCompanies] = useState(false);

  const financeData = analysis.financeData;

  if (!financeData) {
    return null;
  }

  const companies = financeData.companies || [];
  const companyEntities = analysis.entities.filter(
    (e) => e.type === "company"
  );

  // Merge company data from entities and financeData
  const mergedCompanies = companies.length > 0 ? companies : companyEntities.map((e) => ({
    name: e.name,
    ticker: e.metadata?.ticker || "",
    exchange: e.metadata?.exchange || "",
    color: e.metadata?.color || "#6B7280",
    sector: e.metadata?.sector || "",
    marketCap: e.metadata?.marketCap,
    marketCapRaw: undefined as number | undefined,
    keyMetric: undefined as string | undefined,
    recentNews: undefined as string | undefined,
  }));

  // Sort by market probability (descending) then by marketCapRaw
  const sortedCompanies = [...mergedCompanies].sort((a, b) => {
    const priceA = getCompanyPrice(a.name, markets);
    const priceB = getCompanyPrice(b.name, markets);
    if (priceA !== null && priceB !== null) return priceB - priceA;
    if (priceA !== null) return -1;
    if (priceB !== null) return 1;
    return (b.marketCapRaw || 0) - (a.marketCapRaw || 0);
  });

  const displayCompanies = showAllCompanies
    ? sortedCompanies
    : sortedCompanies.slice(0, 8);
  const hasMoreCompanies = sortedCompanies.length > 8;

  const sectors = financeData.sectors || [];
  const upcomingEvents = (financeData.upcomingEvents || []).filter(
    (e) => !isPastDate(e.date)
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-[#18181D] rounded-[5px] border border-[#1A1A1A] overflow-hidden">
        <div className="px-4 py-3 bg-[#18181D] border-b border-[#1A1A1A]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-gray-400" />
              <span className="text-gray-100 text-sm font-inter-tight font-bold truncate">
                {analysis.widgetConfig.title || "Market Cap Race"}
              </span>
            </div>
            {financeData.timeframe && (
              <span className="text-gray-500 text-xs font-inter-tight flex-shrink-0">
                {financeData.timeframe}
              </span>
            )}
          </div>
        </div>

        <div className="p-4">
          {/* Metric info */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-400 text-xs font-inter-tight">
              Comparing by
            </span>
            <span className="text-emerald-400 text-xs font-inter-tight font-bold">
              {financeData.metric}
            </span>
          </div>

          {/* Sector breakdown */}
          {sectors.length > 0 && (
            <div className="mt-3">
              <div className="flex items-center gap-1 mb-2">
                <BarChart3 className="w-3 h-3 text-gray-500" />
                <span className="text-gray-500 text-[10px] font-inter-tight uppercase tracking-wider">
                  Sectors
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {sectors.map((sector, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-0.5 rounded-[5px] text-[10px] font-inter-tight font-bold text-white"
                    style={{ backgroundColor: `${sector.color}40`, color: sector.color }}
                  >
                    {sector.name} ({sector.companyCount})
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Company Rankings */}
      {sortedCompanies.length > 0 && (
        <div className="bg-[#18181D] border border-[#1A1A1A] rounded-[5px] overflow-hidden hover:border-gray-500 transition-colors">
          <div className="bg-[#18181D] border-b border-[#1A1A1A] px-4 py-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-400" />
              <h3 className="text-white font-inter-tight font-bold text-sm uppercase tracking-wider">
                Companies
              </h3>
              <span className="text-gray-500 text-xs font-inter-tight">
                ({sortedCompanies.length})
              </span>
            </div>
          </div>
          <div className="p-3 space-y-2 max-h-[400px] overflow-y-auto scrollbar-hide">
            {displayCompanies.map((company, idx) => {
              const price = getCompanyPrice(company.name, markets);
              const rankColors = [
                "text-[#FFD700]",
                "text-[#C0C0C0]",
                "text-[#CD7F32]",
              ];

              return (
                <div
                  key={idx}
                  className="flex items-center gap-3 p-2.5 rounded-[5px] bg-[#0f0f12] border border-[#1A1A1A] hover:border-gray-500 transition-colors"
                >
                  {/* Rank */}
                  <span
                    className={`w-5 text-center font-monument font-bold text-sm ${rankColors[idx] || "text-gray-500"}`}
                  >
                    {idx + 1}
                  </span>

                  {/* Company icon */}
                  <div
                    className="w-8 h-8 rounded-[5px] flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                    style={{ backgroundColor: company.color }}
                  >
                    {company.ticker
                      ? company.ticker.slice(0, 2)
                      : company.name.charAt(0)}
                  </div>

                  {/* Name & details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-white font-satoshi font-bold text-sm truncate">
                        {company.name}
                      </p>
                      {company.ticker && (
                        <span className="text-gray-500 text-[10px] font-inter-tight flex-shrink-0">
                          {company.ticker}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {company.marketCap && (
                        <span className="text-gray-400 text-xs font-inter-tight">
                          {company.marketCap}
                        </span>
                      )}
                      {company.sector && (
                        <span className="text-gray-500 text-[10px] font-inter-tight">
                          {company.sector}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Market probability */}
                  {price !== null && (
                    <div className="text-right flex-shrink-0">
                      <span className="text-white font-monument font-bold text-sm">
                        {Math.round(price * 100)}%
                      </span>
                    </div>
                  )}
                </div>
              );
            })}

            {hasMoreCompanies && (
              <button
                onClick={() => setShowAllCompanies(!showAllCompanies)}
                className="w-full flex items-center justify-center gap-1 py-2 text-gray-400 hover:text-white text-xs font-inter-tight transition-colors"
              >
                {showAllCompanies ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    Show less
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    Show {sortedCompanies.length - 8} more
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Upcoming Events */}
      {upcomingEvents.length > 0 && (
        <div className="bg-[#18181D] border border-[#1A1A1A] rounded-[5px] p-4">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-4 h-4 text-emerald-400" />
            <h3 className="text-white font-inter-tight font-bold text-sm">
              Upcoming Events
            </h3>
          </div>
          <div className="space-y-2 text-xs">
            {upcomingEvents.slice(0, 5).map((event, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className={`w-1.5 h-1.5 rounded-[5px] flex-shrink-0 ${
                      event.impact === "high"
                        ? "bg-red-400"
                        : event.impact === "medium"
                          ? "bg-yellow-400"
                          : "bg-gray-400"
                    }`}
                  />
                  <span className="text-gray-400 font-satoshi truncate">
                    {event.event}
                  </span>
                </div>
                <span className="text-emerald-400 font-inter-tight font-bold flex-shrink-0 ml-2">
                  {event.date}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Historical Note */}
      {financeData.historicalNote && (
        <div className="bg-[#0f0f12] border border-[#1A1A1A] rounded-[5px] p-3">
          <div className="flex items-start gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
            <p className="text-gray-400 text-[10px] font-inter-tight leading-relaxed">
              {financeData.historicalNote}
            </p>
          </div>
        </div>
      )}

      {/* AI Confidence */}
      <div className="px-1">
        <div className="flex items-center justify-between mb-1">
          <span className="text-gray-400 text-[10px] font-inter-tight">
            AI Confidence
          </span>
          <span className="text-gray-300 text-[10px] font-inter-tight font-bold">
            {analysis.confidence}%
          </span>
        </div>
        <div className="h-1 bg-[#18181D] rounded-[5px] overflow-hidden">
          <div
            className="h-full rounded-[5px]"
            style={{
              width: `${analysis.confidence}%`,
              backgroundColor: "#10B981",
            }}
          />
        </div>
      </div>

      {/* AI Reasoning */}
      {analysis.reasoning && (
        <details className="group">
          <summary className="flex items-center gap-2 text-gray-500 text-xs font-inter-tight cursor-pointer hover:text-gray-300 transition-colors">
            <Zap className="w-3 h-3" />
            <span>Why this widget?</span>
          </summary>
          <p className="mt-2 text-gray-400 text-xs leading-relaxed pl-5">
            {analysis.reasoning}
          </p>
        </details>
      )}
    </div>
  );
}

function isPastDate(dateStr: string): boolean {
  try {
    return new Date(dateStr) < new Date();
  } catch {
    return false;
  }
}
